import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useGoBack } from "../../hooks/useGoBack";
import { motion, AnimatePresence } from "framer-motion";
import OTPInput from "../../components/Input/OTPInput";
import { apiGet, apiPost } from "../../apiService";
import { getErrorMessage } from "../../utils/errorHelper";
import { connectSocket } from "../../services/socket";

// Assuming you are using the same stylesheet for the auth layouts
import "./OTPVerification.scss";

// --- Centralized SVG Icons ---
const Icons = {
  Shield: () => (
    <svg
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      strokeWidth="2"
      width="1.2em"
      height="1.2em"
    >
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
    </svg>
  ),
  ChevronLeft: () => (
    <svg
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      strokeWidth="2.5"
      width="1.2em"
      height="1.2em"
    >
      <polyline points="15 18 9 12 15 6"></polyline>
    </svg>
  ),
};

// --- Helper UI Components ---
const AuthCard = ({ children }) => (
  <motion.div
    className="auth-card"
    initial={{ opacity: 0, y: 30, scale: 0.98 }}
    animate={{ opacity: 1, y: 0, scale: 1 }}
    transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
  >
    {children}
  </motion.div>
);

const CoreEngineVisual = () => (
  <div className="core-engine">
    <svg viewBox="0 0 400 400" fill="none" className="engine-svg">
      <defs>
        <filter id="glow-light" x="-20%" y="-20%" width="140%" height="140%">
          <feGaussianBlur stdDeviation="10" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
        <linearGradient id="circle-grad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#4f46e5" stopOpacity="0.2" />
          <stop offset="100%" stopColor="#0ea5e9" stopOpacity="0.05" />
        </linearGradient>
      </defs>

      {/* Dynamic Background Elements */}
      <circle
        cx="200"
        cy="200"
        r="140"
        stroke="url(#circle-grad)"
        strokeWidth="1"
        strokeDasharray="10 20"
        className="anim-spin"
      />
      <path
        d="M50 200 Q 125 100, 200 200 T 350 200"
        stroke="#e0e7ff"
        strokeWidth="2"
        fill="none"
        className="anim-float"
      />

      {/* Central Identity Node */}
      <g filter="url(#glow-light)">
        <rect
          x="165"
          y="165"
          width="70"
          height="70"
          rx="20"
          fill="#ffffff"
          stroke="#4f46e5"
          strokeWidth="2.5"
          className="anim-pulse"
        />
        <path
          d="M185 200 L195 210 L215 190"
          stroke="#4f46e5"
          strokeWidth="4"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>

      {/* Satellite Tokens */}
      <circle
        cx="100"
        cy="100"
        r="8"
        fill="#4f46e5"
        className="anim-float"
        style={{ animationDelay: "0.2s" }}
      />
      <circle
        cx="300"
        cy="100"
        r="6"
        fill="#38bdf8"
        className="anim-float"
        style={{ animationDelay: "0.8s" }}
      />
      <circle
        cx="200"
        cy="320"
        r="10"
        fill="#a5b4fc"
        className="anim-float"
        style={{ animationDelay: "0.5s" }}
      />
    </svg>
    <div className="engine-text">
      <h2>Security Verification</h2>
      <p>Protecting your institutional identity.</p>
    </div>
  </div>
);

// --- Main OTP Component ---
const OTPVerification = () => {
  const navigate = useNavigate();
  const goBack = useGoBack();
  const location = useLocation();

  const { email, userId, type } = location.state || {};
  const [otp, setOtp] = useState("");
  const [otpError, setOtpError] = useState("");
  const [notification, setNotification] = useState("");
  const [notificationType, setNotificationType] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleVerifyOTP = async (e) => {
    e.preventDefault();

    if (!otp.trim()) {
      setOtpError("OTP is required");
      return;
    }

    setIsSubmitting(true);
    setOtpError("");
    setNotification("");

    try {
      let response;

      // 2FA FLOW
      if (type === "2FA") {
        response = await apiPost("user", "/2fa/verify-otp", {
          userId: userId,
          token: otp,
          type: "2FA",
        });
      }
      // LOGIN / FORGOT OTP
      else {
        response = await apiPost("user", "/verify-otp", {
          email,
          otp,
          type,
        });
      }

      console.log("Type:", type);
      console.log("UserID:", userId);
      console.log("Email:", email);
      console.log("OTP value:", otp);

      if (response?.status === "success") {
        // =========================
        // FORGOT PASSWORD FLOW
        // =========================
        if (type === "FORGOT") {
          // The OTP is consumed on success; carry the single-use resetToken
          // (and its TTL) to the new-password screen. replace:true so Back
          // can't land on this screen with an already-consumed OTP.
          const { resetToken, expiresIn } = response.data || {};
          navigate("/change-password", {
            state: { email, resetToken, expiresIn },
            replace: true,
          });
          return;
        }

        const { token, user, message } = response.data || {};

        // Clear old auth
        localStorage.removeItem("authToken");
        localStorage.removeItem("user");

        // Save token
        if (token) {
          localStorage.setItem("authToken", token);
        }

        // Save user safely
        if (user) {
          localStorage.setItem("user", JSON.stringify(user));
          localStorage.setItem("companyName", user.companyName || "");
          localStorage.setItem("userType", user.userType || "");

          connectSocket(user);
        }

        // Show success notification
        setNotification(message || "OTP verified successfully!");
        setNotificationType("success");

        // =========================
        // FETCH PERMISSIONS
        // =========================
        try {
          const sidebarRes = await apiGet("user", "/modules/sidebar");

          if (sidebarRes?.status === "success") {
            localStorage.setItem(
              "permissions",
              JSON.stringify(sidebarRes.data),
            );

            localStorage.setItem("permissionsLoaded", "true");
          }
        } catch (err) {
          console.error("Permission fetch error:", err);
        }

        // =========================
        // ROLE BASED REDIRECT
        // =========================
        if (user?.userType === "Internal") {
          navigate("/admin/dashboard");
        } else {
          navigate("/dashboard");
        }
      } else {
        const msg = response?.message || "Invalid OTP. Please try again.";
        setOtpError(msg);
        setNotification(msg);
        setNotificationType("error");

        setTimeout(() => {
          setNotification("");
        }, 3000);
      }
    } catch (err) {
      const msg = getErrorMessage(err, "Invalid OTP");

      console.error("OTP Verification Error:", msg);

      setOtpError(msg);
      setNotification(msg);
      setNotificationType("error");

      // Expired OTP in the forgot flow → restart from step 1.
      if (type === "FORGOT" && /expired/i.test(msg)) {
        setTimeout(() => {
          navigate("/forgot-password", { replace: true });
        }, 1800);
        return;
      }

      setTimeout(() => {
        setNotification("");
        setOtpError("");
      }, 3000);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="page-auth-container" id="otp-verification">
      {/* Left Panel: Branding matching Login Screen */}
      <section className="left-branding">
        <CoreEngineVisual />
      </section>

      {/* Right Panel: OTP Form */}
      <main className="right-interface">
        {/* Animated Floating Notification matching Login Screen */}
        <AnimatePresence>
          {notification && (
            <motion.div
              className={`notification-bar notify-${notificationType}`}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
            >
              <Icons.Shield /> {notification}
            </motion.div>
          )}
        </AnimatePresence>

        <AuthCard>
          <button className="back-btn" onClick={goBack}>
            <Icons.ChevronLeft /> Back
          </button>

          <header className="header">
            <h1>OTP Verification</h1>
            <p>
              {type === "2FA"
                ? "Enter the 6-digit code from your Authenticator app"
                : "Enter the 6-digit code sent to your email"}
            </p>
          </header>

          <form onSubmit={handleVerifyOTP} className="form-fields">
            <div className="input-field">
              <div className="label-box">
                <label>Security Code</label>
              </div>
              <div
                className="control-box"
                style={{
                  display: "flex",
                  justifyContent: "center",
                  marginTop: "1rem",
                }}
              >
                <OTPInput
                  numInputs={6}
                  value={otp}
                  onChange={(value) => setOtp(value)}
                  isDisabled={isSubmitting}
                />
              </div>
              {/* Inline input error matching the Login Screen's error style */}
              {otpError && (
                <div
                  className="error-text"
                  style={{ textAlign: "center", marginTop: "1rem" }}
                >
                  {otpError}
                </div>
              )}
            </div>

            <div className="action-submit" style={{ marginTop: "1rem" }}>
              <button type="submit" disabled={isSubmitting}>
                {isSubmitting ? "Verifying..." : "Verify & Continue"}
              </button>
            </div>
          </form>
        </AuthCard>
      </main>
    </div>
  );
};

export default OTPVerification;
